import { useEffect } from 'react';
import { useIMask } from 'react-imask';
import classnames from 'classnames';
import IconDate from './icon_calendar.svg?react';
import s from './index.module.scss';

/**
 * @param {value} - value 'yyyy-mm-dd' or null
 * @param {onChange} - (newValue: 'yyyy-mm-dd' | null) => void
 * @param {onFocusIn} - fires once input focused: () => void
 * @param {onFocusOut} - fires once input out of focus: () => void
 * @param {placeholder} - any string.
 * @param {name} - unique string.
 * @param {disabled} - input state.
 */

export const DateInput = ({
  value,
  onChange,
  onFocusIn,
  onFocusOut,
  placeholder = 'dd.mm.yyyy',
  name,
  disabled,
}) => {
  const { ref: inputRef, maskRef } = useIMask(
    {
      mask: Date,
      pattern: 'd.`m.`Y', // dd.mm.yyyy
      lazy: true,
      autofix: true,
      format: date => {
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}.${mm}.${yyyy}`;
      },
      parse: str => {
        const [dd, mm, yyyy] = str.split('.');
        return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      },
    },
    {
      // we handle onChange only on blur, so no handlers here
    },
  );

  // value (yyyy-mm-dd) -> input (dd.mm.yyyy)
  useEffect(() => {
    const mask = maskRef.current;
    if (!mask) return;

    if (!value) {
      if (mask.value !== '') mask.value = '';
      return;
    }

    const [yyyy, mm, dd] = value.split('-');
    if (!yyyy || !mm || !dd) {
      mask.value = '';
      return;
    }

    const display = `${dd.padStart(2, '0')}.${mm.padStart(2, '0')}.${yyyy}`;
    if (mask.value !== display) {
      mask.value = display;
    }
  }, [value, maskRef]);

  const handleBlur = () => {
    const mask = maskRef.current;

    if (!mask) {
      onFocusOut?.();
      return;
    }

    const raw = mask.value.trim();
    if (!raw) {
      if (value !== null) onChange(null);
      onFocusOut?.();
      return;
    }

    const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(raw);
    if (!match) {
      mask.value = '';
      if (value !== null) onChange(null);
      onFocusOut?.();
      return;
    }

    const [, ddStr, mmStr, yyyyStr] = match;
    const day = Number(ddStr);
    const month = Number(mmStr);
    const year = Number(yyyyStr);

    const date = new Date(year, month - 1, day);
    const isValid =
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (!isValid) {
      mask.value = '';
      if (value !== null) onChange(null);
      onFocusOut?.();
      return;
    }

    const iso = `${yyyyStr}-${mmStr}-${ddStr}`;
    if (iso !== value) {
      onChange(iso);
    }

    onFocusOut?.();
  };

  const handleFocus = () => {
    onFocusIn?.();
  };

  return (
    <div className={classnames(s.wrapper, 'date_input__wrapper')}>
      <div className={classnames(s.input_wrapper, 'date_input__input_wrapper')}>
        <input
          ref={inputRef}
          type="text"
          name={name}
          className={s.input}
          placeholder={placeholder}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
        />
        <IconDate className={classnames(s.icon, value && s.icon_active)} />
      </div>
    </div>
  );
};
